// nextActionEngine — pure computation of the single most important next action
// for the Next_Action_Banner.
//
// Given the active stage result and the classified events list, determines
// which pending action has the nearest upcoming deadline. Ties (same deadline)
// are broken by chronological event order (earlier in the sorted events array
// wins). When no pending action exists, returns an idle result with a message
// indicating time until the next action or "Trip complete".
//
// The instruction text is always ≤80 characters. When a digital action is
// available (navigation, booking, document, rideshare), a contextual action
// button is included.
//
// Validates: Requirements 1.3, 1.6, 6.1, 6.2, 6.5

import { STAGES, STAGE_LABELS } from './journeyStageEngine';

// ── Constants ───────────────────────────────────────────────
const MAX_INSTRUCTION_LENGTH = 80;
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

// AT_DESTINATION can span days, unlike every other (minutes-to-hours) stage.
// A hotel checkout or return flight 2 days out is real, but it isn't "the
// next action" in the GPS sense — only something imminent should occupy the
// single-instruction banner. Anything farther out than this leaves the
// banner idle (letting the destination-window's dining-moment prompt take
// that slot instead) until it actually becomes near-term.
const AT_DESTINATION_LOOKAHEAD_MS = 4 * HOUR_MS;

// ── Action type detection ───────────────────────────────────

/**
 * Map of stage → digital action type. Stages that have an associated
 * digital action the traveler can initiate get a button in the banner.
 */
const STAGE_ACTION_TYPES = {
  [STAGES.DEPARTURE_FROM_ORIGIN]: 'navigation',
  [STAGES.NAVIGATE_TO_GATE]: 'navigation',
  [STAGES.ARRIVAL_NAVIGATION]: 'navigation',
  [STAGES.GROUND_TRANSPORT]: 'rideshare',
};

/**
 * Map of stage → action button label.
 */
const STAGE_ACTION_LABELS = {
  [STAGES.DEPARTURE_FROM_ORIGIN]: 'Get Directions',
  [STAGES.NAVIGATE_TO_GATE]: 'Get Directions',
  [STAGES.ARRIVAL_NAVIGATION]: 'Get Directions',
  [STAGES.GROUND_TRANSPORT]: 'Call Ride',
};

/**
 * Determine if an event type has a digital action available.
 * Non-stage events (hotel, carRental, meeting) may also have actions.
 */
function getActionButton(event, stage) {
  // Stage events use the stage-based action map
  if (event.type === 'stage') {
    const actionType = STAGE_ACTION_TYPES[event.stage];
    if (actionType) {
      return {
        label: STAGE_ACTION_LABELS[event.stage],
        actionType,
        payload: buildPayload(event, actionType),
      };
    }
    return null;
  }

  // Non-stage events: booking confirmations and documents
  if (event.type === 'hotel') {
    return {
      label: 'View Booking',
      actionType: 'booking',
      payload: { eventId: event.id, sourceEvent: event.sourceEvent },
    };
  }

  if (event.type === 'carRental') {
    return {
      label: 'View Booking',
      actionType: 'booking',
      payload: { eventId: event.id, sourceEvent: event.sourceEvent },
    };
  }

  if (event.type === 'meeting') {
    return {
      label: 'View Details',
      actionType: 'document',
      payload: { eventId: event.id, sourceEvent: event.sourceEvent },
    };
  }

  return null;
}

/**
 * Build the payload for an action button based on event data.
 */
function buildPayload(event, actionType) {
  const payload = { eventId: event.id };

  if (event.stageInputs) {
    const { flight, flightStatus, meta } = event.stageInputs;
    if (actionType === 'navigation') {
      payload.airport = flight?.departureAirport || flight?.arrivalAirport || null;
      payload.gate = meta?.gate || flightStatus?.departure?.gate || null;
      payload.terminal = meta?.terminal || flightStatus?.departure?.terminal || null;
    }
    if (actionType === 'rideshare') {
      payload.airport = flight?.arrivalAirport || null;
    }
  }

  return payload;
}

// ── Instruction generation ──────────────────────────────────

/**
 * Generate a human-readable instruction for the given event.
 * Always ≤80 characters.
 *
 * @param {Object} event - The classified event
 * @param {Date} now - Current time
 * @returns {string} Instruction text ≤80 chars
 */
function generateInstruction(event, now) {
  const timeUntil = formatTimeUntil(event.scheduledStart, now);
  let instruction = '';

  if (event.type === 'stage') {
    instruction = buildStageInstruction(event, timeUntil);
  } else if (event.type === 'hotel') {
    instruction = `${event.title}${timeUntil ? ' ' + timeUntil : ''}`;
  } else if (event.type === 'carRental') {
    instruction = `${event.title}${timeUntil ? ' ' + timeUntil : ''}`;
  } else if (event.type === 'meeting') {
    instruction = `${event.title}${timeUntil ? ' ' + timeUntil : ''}`;
  } else {
    instruction = `${event.title || 'Next step'}${timeUntil ? ' ' + timeUntil : ''}`;
  }

  return truncateInstruction(instruction);
}

/**
 * Instruction builders for each stage, keyed by stage constant.
 * Each builder receives (meta, timeUntil) and returns the instruction string.
 * Using a map eliminates the switch statement and reduces cyclomatic complexity.
 */
const STAGE_INSTRUCTION_BUILDERS = {
  [STAGES.DEPARTURE_FROM_ORIGIN]: (_meta, timeUntil) =>
    `Leave for airport${timeUntil ? ' ' + timeUntil : ''}`,

  [STAGES.AIRPORT_ENTRY]: (_meta, timeUntil) =>
    `Arrive at airport${timeUntil ? ' ' + timeUntil : ''}`,

  [STAGES.SECURITY_CHECKIN]: (_meta, timeUntil) =>
    `Head to security${timeUntil ? ' ' + timeUntil : ''}`,

  [STAGES.NAVIGATE_TO_GATE]: (meta, timeUntil) => {
    const gate = meta.gate || '';
    const base = gate ? `Head to Gate ${gate}` : 'Navigate to your gate';
    return `${base}${timeUntil ? ' ' + timeUntil : ''}`;
  },

  [STAGES.WAITING_BOARDING]: (meta, timeUntil) => {
    const gate = meta.gate || '';
    const base = gate ? `Board at Gate ${gate}` : 'Boarding soon';
    return `${base}${timeUntil ? ' ' + timeUntil : ''}`;
  },

  [STAGES.IN_FLIGHT]: (_meta, timeUntil) =>
    `Flight departs${timeUntil ? ' ' + timeUntil : ''}`,

  [STAGES.ARRIVAL_NAVIGATION]: (meta) => {
    const gate = meta.gate || '';
    return gate ? `Follow signs from Gate ${gate}` : 'Follow signs to exit';
  },

  [STAGES.GROUND_TRANSPORT]: (_meta, timeUntil) =>
    `Get ground transport${timeUntil ? ' ' + timeUntil : ''}`,
};

/**
 * Build instruction text for a stage event.
 * Uses an instruction map to avoid a large switch statement.
 */
function buildStageInstruction(event, timeUntil) {
  const meta = event.stageInputs?.meta || {};
  const builder = STAGE_INSTRUCTION_BUILDERS[event.stage];

  if (builder) {
    return builder(meta, timeUntil);
  }

  return `${STAGE_LABELS[event.stage] || 'Next step'}${timeUntil ? ' ' + timeUntil : ''}`;
}

/**
 * Format time until an event as a human-readable relative string.
 * Returns empty string if the event is in the past or at the current time.
 *
 * @param {Date} eventTime - The event's scheduled start
 * @param {Date} now - Current time
 * @returns {string} e.g., "in 2h 15m", "in 30 min", "now", or ""
 */
function formatTimeUntil(eventTime, now) {
  if (!eventTime || !now) return '';

  const eventMs = eventTime instanceof Date ? eventTime.getTime() : new Date(eventTime).getTime();
  const nowMs = now instanceof Date ? now.getTime() : new Date(now).getTime();

  if (Number.isNaN(eventMs) || Number.isNaN(nowMs)) return '';

  const diffMs = eventMs - nowMs;

  if (diffMs <= 0) return '';
  if (diffMs < MINUTE_MS) return 'now';

  const totalMinutes = Math.round(diffMs / MINUTE_MS);

  if (totalMinutes < 60) {
    return `in ${totalMinutes} min`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes === 0) {
    return `in ${hours}h`;
  }

  return `in ${hours}h ${minutes}m`;
}

/**
 * Truncate instruction to ≤80 characters, preserving word boundaries.
 */
function truncateInstruction(instruction) {
  if (instruction.length <= MAX_INSTRUCTION_LENGTH) {
    return instruction;
  }

  // Truncate at last space before the limit
  const truncated = instruction.slice(0, MAX_INSTRUCTION_LENGTH);
  const lastSpace = truncated.lastIndexOf(' ');

  if (lastSpace > MAX_INSTRUCTION_LENGTH * 0.5) {
    return truncated.slice(0, lastSpace);
  }

  return truncated;
}

// ── Idle message generation ─────────────────────────────────

/**
 * Generate an idle message when no pending action exists.
 *
 * @param {Object} activeStage - The active stage result
 * @param {Array} events - All classified events
 * @param {Date} now - Current time
 * @returns {string} Idle message
 */
function generateIdleMessage(activeStage, events, now) {
  // Check if trip is complete
  if (activeStage && activeStage.stage === STAGES.TRIP_COMPLETE) {
    return 'Trip complete';
  }

  // Find the next future event to calculate time until
  const nowMs = now.getTime();
  const nextFutureEvent = events.find((e) => {
    const startMs = e.scheduledStart instanceof Date
      ? e.scheduledStart.getTime()
      : new Date(e.scheduledStart).getTime();
    return !Number.isNaN(startMs) && startMs > nowMs;
  });

  if (nextFutureEvent) {
    const startMs = nextFutureEvent.scheduledStart instanceof Date
      ? nextFutureEvent.scheduledStart.getTime()
      : new Date(nextFutureEvent.scheduledStart).getTime();
    const diffMs = startMs - nowMs;
    const totalMinutes = Math.round(diffMs / MINUTE_MS);

    if (totalMinutes < 60) {
      return `Next step in ${totalMinutes} min`;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (minutes === 0) {
      return `Next step in ${hours} hour${hours > 1 ? 's' : ''}`;
    }

    return `Next step in ${hours}h ${minutes}m`;
  }

  // No future events at all. At an active destination this is genuinely
  // "nothing scheduled right now" (still mid-trip), not the trip being
  // over — only say "Trip complete" when the stage engine actually agrees.
  if (activeStage && activeStage.stage === STAGES.AT_DESTINATION) {
    return 'Nothing scheduled right now';
  }

  return 'Trip complete';
}

/**
 * Whether `event` belongs to the active stage. Stage events match by stage,
 * non-stage events match by parentStage if available, otherwise we include
 * current/next events regardless. AT_DESTINATION is a special case: no
 * computeStageTimeline entry (and therefore no Stage_Event) is ever tagged
 * with it — the whole multi-day stay is "at destination," so every
 * non-stage event (hotel/carRental/meeting/dining) within it belongs to
 * this stage.
 */
function belongsToActiveStage(event, activeStageId, nowMs) {
  return (
    (event.type === 'stage' && event.stage === activeStageId) ||
    Boolean(event.parentStage && event.parentStage === activeStageId) ||
    (activeStageId === STAGES.AT_DESTINATION && event.type !== 'stage' && isNearTerm(event, nowMs)) ||
    (event.eventState === 'current' || event.eventState === 'next')
  );
}

/**
 * Whether `event` has already elapsed as of `nowMs`. `eventState==='past'`
 * only ever gets set when a caller has run classifyEvents first (the legacy
 * VerticalJourneyTimeline path) — TripCardFull's live path passes raw
 * buildJourneyEvents output, where eventState is always undefined, so that
 * check alone is a no-op there. Compute it directly from the event's own
 * end (or start, if no end) against `now` so a past hotel check-in/meeting/
 * etc. can't be selected as "pending" just because AT_DESTINATION lets
 * non-stage events through at all (Stage_Events' own current-ness is
 * already implied by being the active stage, so this can only ever exclude
 * genuinely-elapsed events, never a truly current one).
 */
function isEventPast(event, nowMs) {
  if (event.eventState === 'past') return true;
  const endMs = event.scheduledEnd instanceof Date
    ? event.scheduledEnd.getTime()
    : event.scheduledStart instanceof Date
      ? event.scheduledStart.getTime()
      : NaN;
  return !Number.isNaN(endMs) && endMs < nowMs;
}

/** Whether `event` has a resolvable, valid `scheduledStart`. */
function hasValidScheduledStart(event) {
  const startMs = event.scheduledStart instanceof Date
    ? event.scheduledStart.getTime()
    : new Date(event.scheduledStart).getTime();
  return !Number.isNaN(startMs);
}

/** An event is "pending" when it belongs to the active stage, hasn't elapsed, and has a valid start. */
function isPendingEvent(event, activeStageId, nowMs) {
  if (!belongsToActiveStage(event, activeStageId, nowMs)) return false;
  if (isEventPast(event, nowMs)) return false;
  return hasValidScheduledStart(event);
}

// ── Main ────────────────────────────────────────────────────

/**
 * Compute the single most important next action for the Next_Action_Banner.
 *
 * Selects the pending action with the nearest upcoming deadline from the
 * active stage's events. Ties (same deadline) are broken by chronological
 * event order (position in the events array).
 *
 * @param {Object|null} activeStage - Result from `resolveTripStages` or
 *   `resolveCurrentStage`. Shape: `{ stage, progress, meta, ... }`.
 *   Pass `null` for pre-trip or when no active stage exists.
 * @param {Array<Object>} events - Classified events from `classifyEvents`.
 *   Each event has `{ id, type, stage, eventState, scheduledStart,
 *   scheduledEnd, title, ... }`.
 * @param {Date} now - Current clock value.
 * @returns {Object} NextAction result:
 *   `{ instruction, deadline, actionButton?, isIdle, idleMessage? }`
 */
export function computeNextAction(activeStage, events, now) {
  const safeNow = now instanceof Date && !Number.isNaN(now.getTime())
    ? now
    : new Date();

  const safeEvents = Array.isArray(events) ? events : [];

  // If no active stage or trip is complete, return idle
  if (!activeStage || activeStage.stage === STAGES.TRIP_COMPLETE) {
    const idleMessage = generateIdleMessage(activeStage, safeEvents, safeNow);
    return {
      instruction: idleMessage,
      deadline: safeNow,
      isIdle: true,
      idleMessage,
    };
  }

  const nowMs = safeNow.getTime();
  const activeStageId = activeStage.stage;

  // Filter to pending events: events in the active stage that haven't passed
  // "Pending" means: eventState is 'current' or 'next' or 'future', and
  // scheduledStart is not in the past (or is the current stage event).
  const pendingEvents = safeEvents.filter((event) => isPendingEvent(event, activeStageId, nowMs));

  // If no pending events, return idle
  if (pendingEvents.length === 0) {
    const idleMessage = generateIdleMessage(activeStage, safeEvents, safeNow);
    return {
      instruction: idleMessage,
      deadline: safeNow,
      isIdle: true,
      idleMessage,
    };
  }

  // Select the event with the nearest upcoming deadline.
  // "Deadline" is the scheduledStart (when the action needs to happen).
  // Ties broken by array order (chronological event order from buildJourneyEvents).
  let selectedEvent = pendingEvents[0];
  let selectedDeadlineMs = getDeadlineMs(selectedEvent);

  for (let i = 1; i < pendingEvents.length; i++) {
    const candidateMs = getDeadlineMs(pendingEvents[i]);

    // Nearest deadline wins; ties keep the earlier (lower index) event
    if (candidateMs < selectedDeadlineMs) {
      selectedEvent = pendingEvents[i];
      selectedDeadlineMs = candidateMs;
    }
  }

  const instruction = generateInstruction(selectedEvent, safeNow);
  const deadline = new Date(selectedDeadlineMs);
  const actionButton = getActionButton(selectedEvent, activeStageId);

  const result = {
    instruction,
    deadline,
    isIdle: false,
  };

  if (actionButton) {
    result.actionButton = actionButton;
  }

  return result;
}

// ── Helpers ─────────────────────────────────────────────────

/**
 * Extract the deadline timestamp from an event.
 * Uses scheduledStart as the deadline (when the action needs to happen).
 */
function getDeadlineMs(event) {
  const start = event.scheduledStart;
  if (start instanceof Date) return start.getTime();
  const ms = new Date(start).getTime();
  return Number.isNaN(ms) ? Infinity : ms;
}

/**
 * True when `event` has already started (its deadline has passed but it
 * hasn't been filtered out as fully elapsed yet) or starts within
 * AT_DESTINATION_LOOKAHEAD_MS. Used to keep far-future destination-window
 * events (a hotel checkout two days out) from occupying the single-action
 * banner — see AT_DESTINATION_LOOKAHEAD_MS.
 */
function isNearTerm(event, nowMs) {
  const startMs = getDeadlineMs(event);
  if (!Number.isFinite(startMs)) return false;
  return startMs - nowMs <= AT_DESTINATION_LOOKAHEAD_MS;
}

export default computeNextAction;
